import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  completeJob,
  createJobConversation,
  getJob,
  listQuotes,
  withdrawFromJob,
} from "../../lib/api/contractor";
import { ApiError } from "../../lib/api/client";
import { useContractor } from "../../lib/auth";
import {
  canMarkComplete,
  canStartWork,
  isJobCompleted,
  isJobOpenForQuotes,
  jobCategoryDisplay,
  parseJobBookingMode,
  parseJobStatus,
  parseOperatingMode,
  parseQuoteStatus,
  urgencyLabel,
} from "../../lib/types/enums";
import type { Job, Quote } from "../../lib/types/models";
import { JobRating } from "../chat/JobRating";
import { budgetRange, dateOnly, dateTime, km, money, relative } from "../../lib/format";
import { useGeolocation } from "../../lib/useGeolocation";
import { JobStatusPill } from "../../components/JobCard";
import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHead,
  Skeleton,
  SpecList,
  StatusPill,
  useToast,
} from "@ui/react";
import { ChatIcon, ChevronLeftIcon, InboxIcon, PinIcon } from "@ui/react/icons";
import { SendQuoteModal } from "../chat/SendQuoteModal";
import { startWorkOnJob } from "./startWork";
import { errorMessage } from "../../lib/errorMessage";

/**
 * The two named failures `POST /jobs/:jobId/conversation` can return that the
 * contractor can actually act on. Everything else falls through to the generic
 * copy from `errorMessage` — which already covers CONTRACTOR_BLOCKED's sibling
 * states (NOT_VERIFIED, PAYOUT_SETUP_REQUIRED) in the same words as the rest of
 * the app.
 *
 * `WRONG_MODE` is ambiguous over the wire: the endpoint returns it both for a
 * bookable contractor and for a direct-booking job, and the code alone cannot
 * tell them apart. `bookable` resolves it from what this client already knows
 * about itself, so each case gets the sentence that names the actual fix.
 */
function explainOpenFailure(error: unknown, bookable: boolean): { title: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.isBlocked) {
      return {
        title: "Your account is on hold for new work",
        detail:
          "Recent reviews have paused you from quoting on or accepting new jobs, so new conversations are closed too. Contact support to resolve this.",
      };
    }
    if (error.code === "WRONG_MODE") {
      return bookable
        ? {
            title: "Switch to marketplace mode to message about this job",
            detail:
              "You're set up for direct bookings, which don't use the marketplace. Change your operating mode in booking setup first.",
          }
        : {
            title: "This job is a direct booking",
            detail:
              "Direct bookings already have their own conversation with the homeowner — you'll find it in Messages.",
          };
    }
  }
  return { title: "Couldn't open the conversation", detail: errorMessage(error) };
}

/**
 * One job, everything known about it, and the two transitions a contractor can
 * drive from here.
 *
 * `GET /jobs/:jobId` is authorized: a job that is neither assigned to this
 * contractor nor still Pending answers 404. That is the ordinary outcome of
 * opening a stale link or a job someone else won — so it reads as "no longer
 * available" rather than as a failure with a retry button.
 */
export function JobDetailsScreen() {
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const contractor = useContractor();
  const geo = useGeolocation();
  const toast = useToast();
  const [state, setState] = useState<{ loading: boolean; error: unknown; data: Job | null }>({
    loading: true,
    error: null,
    data: null,
  });
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [openError, setOpenError] = useState<unknown>(null);
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [myQuote, setMyQuote] = useState<Quote | null>(null);

  // Guards against a slow response for a PREVIOUS job painting over the current
  // one. The notification bell navigates between job ids without unmounting this
  // screen, so the effect re-runs while the earlier request is still in flight —
  // and the contractor would end up acting on someone else's job.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // The quote is supplementary — the same trade ActiveJobsScreen makes:
      // losing the whole screen because /quotes failed would be the worse
      // outcome, so a failure just means "not quoted yet" here.
      const [data, quotes] = await Promise.all([
        getJob(jobId, geo.position?.lat, geo.position?.lng),
        listQuotes().catch(() => [] as Quote[]),
      ]);
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error: null, data });
      setMyQuote(
        quotes.find(
          (quote) => quote.jobId === jobId && parseQuoteStatus(quote.status) === "pending",
        ) ?? null,
      );
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error, data: null });
    }
  }, [jobId, geo.position]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Opens the thread and goes to it. The endpoint is idempotent — 201 the first
   * time, 200 with the same thread after — so a stale click or a retry lands on
   * the same conversation rather than creating a second one; `opening` is still
   * held for the round trip so a double-click cannot fire two navigations.
   */
  const onAsk = async () => {
    if (opening) return;
    setOpening(true);
    setOpenError(null);
    try {
      const conversation = await createJobConversation(jobId);
      navigate(`/messages/${conversation.conversationId}`);
    } catch (error) {
      setOpenError(error);
      // Left true on success: the screen is unmounting into the thread, and
      // re-enabling the button first only invites a second click on the way out.
      setOpening(false);
    }
  };

  const onStart = async () => {
    const job = state.data;
    if (!job) return;
    setBusy(true);
    setActionError(null);
    try {
      // Null means the appointment door was taken, which answers with the
      // appointment rather than the moved job — so re-read it.
      const updated = await startWorkOnJob(job);
      if (updated) setState({ loading: false, error: null, data: updated });
      else await load();
      toast.show("Work started. The homeowner has been notified.");
    } catch (error) {
      setActionError(error);
    } finally {
      setBusy(false);
      setConfirmStart(false);
    }
  };

  const onComplete = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await completeJob(jobId);
      setConfirmComplete(false);
      setState({ loading: false, error: null, data: updated });
      // The backend moves the job to PendingUserSignOff, NOT Completed — saying
      // "completed" here would promise a payout that has not been released yet.
      toast.show("Job marked as complete. Awaiting homeowner sign-off.");
    } catch (error) {
      setConfirmComplete(false);
      setActionError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Hands the job back to the marketplace. The response is the job as it now
   * stands — unassigned, back at Quoted or Pending — so it replaces the local
   * copy rather than triggering a re-read: `GET /jobs/:jobId` would still
   * answer, but with the masked, not-mine shape, and the screen would appear to
   * reload into someone else's job.
   */
  const onWithdraw = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await withdrawFromJob(jobId);
      setConfirmWithdraw(false);
      setState({ loading: false, error: null, data: updated });
      setMyQuote(null);
      toast.show("You've withdrawn from this job. The homeowner has been told.");
    } catch (error) {
      setConfirmWithdraw(false);
      setActionError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The screen, whatever it currently knows.
   *
   * Every state below used to return its own fragment, so the skeleton, the
   * error and the "job withdrawn" state each rendered a document with no <h1>
   * on it at all — and no way back to the feed except the browser button, which
   * is the exact gap the page head was added to close (this being the
   * marketplace's most visited detail screen, and the only one that lacked a
   * way back). The title is the only part that has to wait for the job.
   *
   * `mine` is the last argument, and false everywhere the job has not loaded:
   * a job he owns was reached from Active work, and telling him he is browsing
   * the marketplace — then dropping him into a feed of other people's jobs as
   * the only way out — is wrong on both counts. Ownership is unknown until the
   * job arrives, so the loading, error and 404 branches keep the feed's wording
   * (which chrome-contract pins on the 404).
   */
  const screen = (title: string, body: ReactNode, actions?: ReactNode, mine = false) => (
    <div className="stack stack--loose">
      <PageHead
        eyebrow={mine ? "Active work" : "Find jobs"}
        title={title}
        back={
          <Link className="back-link" to={mine ? "/active" : "/jobs"}>
            <ChevronLeftIcon />
            {mine ? "All active work" : "All jobs"}
          </Link>
        }
        actions={actions}
      />
      {body}
    </div>
  );

  if (state.loading) {
    return screen(
      "Job details",
      <div className="stack" role="status">
        <span className="sr-only">Loading job details</span>
        <Skeleton height="3rem" />
        <Skeleton height="14rem" />
        <Skeleton height="8rem" />
      </div>,
    );
  }

  if (state.error) {
    const missing = state.error instanceof ApiError && state.error.status === 404;
    return screen(
      "Job details",
      missing ? (
        // No action button: the page head's back link is 60px above it and goes
        // to the same place. Two links to the feed, worded differently, is the
        // screen saying one thing twice.
        <EmptyState icon={<InboxIcon />} title="This job is no longer available">
          It may have been withdrawn, or another contractor may already have been
          booked for it.
        </EmptyState>
      ) : (
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      ),
    );
  }

  const job = state.data;
  if (!job) {
    return screen(
      "Job details",
      <ErrorState message={errorMessage(new Error("Job not found."))} onRetry={() => void load()} />,
    );
  }

  const status = parseJobStatus(job.status);
  const bookingMode = parseJobBookingMode(job.bookingMode);

  // "I already won this job." contractorId is written only inside the
  // accept-quote transaction, and `GET /jobs/mine` filters on it — so this is
  // the same test the rest of the app uses for a bound contractor.
  const boundToMe = !!job.contractorId && job.contractorId === contractor.contractorId;

  // A direct booking is released from the booking itself, which owns the
  // cancellation policy and the refund — never from here.
  const canWithdraw =
    boundToMe &&
    bookingMode === "marketplace" &&
    (status === "accepted" || status === "scheduled");
  const explained = explainOpenFailure(
    openError,
    parseOperatingMode(contractor.operatingMode) === "bookable",
  );

  const place = [job.location?.address, job.location?.city, job.location?.province, job.location?.postCode]
    .filter(Boolean)
    .join(", ");
  const distance = km(job.distance);
  const postedRelative = relative(job.createdAt);

  return screen(
    job.title,
    <>
      {/* Only when it holds something: the parent `.stack--loose` charges its
          2rem gap for an empty flex item too, which is what put a dead band
          under this screen's head and nowhere else's. */}
      {job.paymentIssue || actionError ? (
        <div className="stack">
          {job.paymentIssue ? (
            <Banner tone="danger" title="Payment issue">
              Payment for this job could not be completed. Our team is looking into it —
              your payout is on hold.
            </Banner>
          ) : null}

          {actionError ? (
            <Banner tone="danger" title="That didn't work">
              {errorMessage(actionError)}
            </Banner>
          ) : null}
        </div>
      ) : null}

      <div className="card card--dense">
        <SpecList
          columns
          items={[
            { label: "Category", value: jobCategoryDisplay(job.category) },
            { label: "Urgency", value: urgencyLabel(job.urgency) || "Not set" },
            {
              label: "Budget",
              value: (
                <span className="amount">
                  {budgetRange(job.budget?.min, job.budget?.max, job.budget?.currency ?? "ZAR")}
                </span>
              ),
            },
            { label: "Quotes received", value: job.quoteCount },
            {
              label: "Posted",
              // `relative()` falls back to a plain date past a month, so the
              // parenthetical would otherwise repeat the date it just printed.
              value: postedRelative && postedRelative !== dateOnly(job.createdAt)
                ? `${dateTime(job.createdAt)} (${postedRelative})`
                : dateTime(job.createdAt),
            },
            {
              label: "Started",
              value: job.startedAt ? dateTime(job.startedAt) : null,
              when: Boolean(job.startedAt),
            },
            {
              label: "Completed",
              value: job.completedAt ? dateTime(job.completedAt) : null,
              when: Boolean(job.completedAt),
            },
          ]}
        />
      </div>

      <div className="card card--dense">
        <div className="stack stack--tight">
          <h2 className="headline-sm">Location</h2>
          {/* The address is its own flex item: as a bare text node it wrapped
              as a whole and left the pin orphaned on a line of its own. */}
          <p className="cluster cluster--nowrap cluster--start muted">
            <PinIcon className="icon-sm" />
            <span className="cluster__text">
              {/* Not mine: contractor-api has already reduced `place` to the
                  suburb, province and post code — no street address to leak.
                  If it left even that off, "No address provided" would now be
                  a lie: the address exists, it is withheld until the job is
                  won. */}
              {place || (boundToMe ? "No address provided" : "Exact address shared once the job is yours.")}
            </span>
          </p>
          {/* `distance` and a street address never arrive together (see
              `Job.distance`), so this and the address above never both show. */}
          {!boundToMe && distance ? <p className="muted">{distance} away</p> : null}
          {/* `place` is also the condition: with no area either, the line above
              has ALREADY said "Exact address shared once the job is yours." and
              this would stack a second, near-identical sentence under it. */}
          {!boundToMe && !job.location?.address && place ? (
            <p className="muted">The full address is shared once the job is yours.</p>
          ) : null}
        </div>
      </div>

      <div className="card card--dense">
        <div className="stack stack--tight">
          <h2 className="headline-sm">Description</h2>
          <p className="muted pre-wrap">
            {job.description || "The homeowner didn't add a description."}
          </p>
        </div>
      </div>

      {job.images && job.images.length > 0 ? (
        <div className="card card--dense">
          <div className="stack">
            <h2 className="headline-sm">Photos</h2>
            <div className="card-grid">
              {job.images.map((src, index) => (
                <img
                  key={src}
                  src={src}
                  // The homeowner supplies no captions, so the only honest
                  // description is which photo this is.
                  alt={`Job photo ${index + 1} of ${job.images?.length ?? 0}`}
                  loading="lazy"
                  className="photo-tile"
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="stack">
        {openError ? (
          // Deliberately NOT hoisted into the banner stack at the top of the
          // page: the action row sits below the description and the photos, so
          // a banner up there is off-screen at the moment of the click. `Banner`
          // is role="status", so it is announced either way.
          <Banner tone="danger" title={explained.title}>
            {explained.detail}
          </Banner>
        ) : null}

        {boundToMe && isJobCompleted(status) ? (
          // Messaging is closed once the job is done — the rating the
          // homeowner left (if any) replaces the entry point to the thread.
          <div className="card card--dense">
            <div className="stack stack--tight">
              <h2 className="headline-sm">Rating</h2>
              <JobRating rating={job.rating} />
            </div>
          </div>
        ) : null}

        <div className="cluster">
          {boundToMe && isJobCompleted(status) ? null : boundToMe && job.conversationId ? (
            // This contractor already won the job, so the thread exists and the
            // job points at it — conversation-api rebinds job.conversationId to
            // the winning conversation inside the accept-quote transaction.
            // Nothing to create; link straight through.
            <Link className="btn btn-secondary" to={`/messages/${job.conversationId}`}>
              <ChatIcon />
              Message the customer
            </Link>
          ) : (
            // Not "Message the customer", because before an accepted quote there
            // is no thread with this contractor in it yet — this button creates
            // one.
            //
            // What was here before linked to `job.conversationId`, which
            // user-api stamps onto every job at creation and which is therefore
            // NEVER empty: the "a conversation opens once you've quoted"
            // fallback beside it could not render, and the link that did render
            // answered 403 for every contractor whose quote had not already been
            // accepted.
            <Button variant="secondary" onClick={() => void onAsk()} loading={opening}>
              <ChatIcon />
              Ask a question
            </Button>
          )}

          {/* `quoted`, not just `pending`: the FIRST quote by anyone flips the
              job, while the feed query, this endpoint and POST /quotes all
              accept both (types.JobIsOpenForQuotes). Gating on `pending` alone
              left most of a live feed with no way to price a job at all — and
              hid this contractor's own "Quoted · R2 400" pill the moment a
              rival quoted, so his own quote looked like it had never been sent.
              Neither Start work nor Mark complete can be offered at either
              status (both need funds held), so this is still the only primary. */}
          {!boundToMe && isJobOpenForQuotes(status) ? (
            myQuote ? (
              <StatusPill tone="pending">
                Quoted · {money(myQuote.amount, myQuote.currency)}
              </StatusPill>
            ) : (
              // Quoting used to live only in the thread header, so the one
              // conversion action on the marketplace took a conversation and a
              // screen change to reach.
              <Button onClick={() => setQuoteOpen(true)}>Send quote</Button>
            )
          ) : null}

          {canStartWork(status, bookingMode) ? (
            <Button onClick={() => setConfirmStart(true)} disabled={busy}>
              Start work
            </Button>
          ) : status === "accepted" || status === "scheduled" ? (
            // Same "nothing held yet" note ActiveJobsScreen shows on these
            // cards — without it the button just vanishes with no signal.
            // "scheduled" is included now that bookingMode distinguishes an
            // unpaid scheduled visit from a paid direct booking, which takes
            // the branch above instead.
            <span className="label-sm muted">Waiting for the homeowner's payment</span>
          ) : null}

          {/* A direct booking is completable from the instant it is paid as far
              as the JOB is concerned, but the server refuses TOO_EARLY until
              the slot's start time has passed — a clock that lives on the
              booking, which this screen never reads. The booking detail screen
              does, and greys the same action out with the date it becomes
              available, so completion is ITS to own; offering it here too gave
              two screens opposite answers about the same job, with a
              payment-release dialog in between. Start work is unaffected: the
              server imposes no clock on it. */}
          {canMarkComplete(status, bookingMode) && bookingMode !== "direct" ? (
            <Button onClick={() => setConfirmComplete(true)} disabled={busy}>
              Mark complete
            </Button>
          ) : null}

          {bookingMode === "direct" && job.bookingId ? (
            <Link className="btn btn-secondary" to={`/bookings/${job.bookingId}`}>
              <InboxIcon />
              Open the booking
            </Link>
          ) : null}

          {/* Mirrors contractor-api's Withdraw guard: the two PRE-hold statuses
              (types.FundsHeld is false for both on a marketplace job) and
              nothing else. Once the homeowner has paid, unwinding the hold is
              their cancellation to make. Until this existed, a contractor who
              had won a job had no exit from it at all — accept, start and
              complete were the only moves — so "complete or cancel these jobs
              first" on the mode switch named two things he could not do. */}
          {canWithdraw ? (
            <Button variant="secondary" onClick={() => setConfirmWithdraw(true)} disabled={busy}>
              Withdraw from this job
            </Button>
          ) : null}
        </div>
      </div>

      {quoteOpen ? (
        <SendQuoteModal
          jobId={jobId}
          onClose={() => setQuoteOpen(false)}
          onSent={(quote) => {
            setQuoteOpen(false);
            setMyQuote(quote);
            toast.show(`Quote for ${money(quote.amount, quote.currency)} sent.`);
          }}
        />
      ) : null}

      {confirmStart ? (
        <ConfirmDialog
          title="Start work on this job?"
          body="The homeowner will be notified that work has begun."
          confirmLabel="Start work"
          confirming={busy}
          onConfirm={() => void onStart()}
          onCancel={() => setConfirmStart(false)}
        />
      ) : null}

      {confirmComplete ? (
        <ConfirmDialog
          title="Mark this job complete?"
          body={
            <>
              <p>
                The homeowner will be asked to review the work and sign off before your
                payment is released.
              </p>
              <p>Only do this once the work is genuinely finished.</p>
            </>
          }
          confirmLabel="Mark complete"
          confirming={busy}
          onConfirm={() => void onComplete()}
          onCancel={() => setConfirmComplete(false)}
        />
      ) : null}

      {confirmWithdraw ? (
        <ConfirmDialog
          title="Withdraw from this job?"
          confirmLabel="Withdraw"
          cancelLabel="Keep the job"
          destructive
          confirming={busy}
          onConfirm={() => void onWithdraw()}
          onCancel={() => setConfirmWithdraw(false)}
          body={
            <>
              <p>
                The job goes back on the marketplace for other contractors to quote on, and
                the homeowner is told in your conversation.
              </p>
              <p>Your quote is withdrawn with it. You can quote again if you change your mind.</p>
            </>
          }
        />
      ) : null}
    </>,
    <JobStatusPill status={job.status} />,
    boundToMe,
  );
}
